import type { Vec3 } from './math';

/**
 * 1-Euro filter (Casiez et al. 2012).
 *
 * Why this and not an EMA or a Kalman filter:
 *  - A fixed EMA forces you to choose between jitter at rest and lag in motion.
 *    For punch detection that trade-off is fatal: enough smoothing to kill
 *    landmark jitter adds ~80 ms of lag to a 120 ms jab.
 *  - A Kalman filter needs a motion model. Limb dynamics during a punch are
 *    strongly non-linear; a constant-velocity model overshoots at the snap-back.
 *  - 1-Euro is adaptive: cutoff rises with speed, so it is heavily smoothed while
 *    you hold guard and nearly transparent while the fist is travelling. Two
 *    intuitive parameters, ~20 flops per landmark.
 */
export class OneEuro {
  private xPrev = 0;
  private dxPrev = 0;
  private tPrev = -1;
  private started = false;

  constructor(
    private minCutoff = 1.2,
    private beta = 0.03,
    private dCutoff = 1.0,
  ) {}

  private static alpha(cutoff: number, dt: number): number {
    const tau = 1 / (2 * Math.PI * cutoff);
    return 1 / (1 + tau / dt);
  }

  filter(x: number, tSec: number): number {
    if (!this.started) {
      this.started = true;
      this.xPrev = x;
      this.tPrev = tSec;
      return x;
    }
    const dt = Math.max(1e-3, tSec - this.tPrev);
    this.tPrev = tSec;

    const dx = (x - this.xPrev) / dt;
    const ad = OneEuro.alpha(this.dCutoff, dt);
    const dxHat = ad * dx + (1 - ad) * this.dxPrev;
    this.dxPrev = dxHat;

    const cutoff = this.minCutoff + this.beta * Math.abs(dxHat);
    const a = OneEuro.alpha(cutoff, dt);
    const xHat = a * x + (1 - a) * this.xPrev;
    this.xPrev = xHat;
    return xHat;
  }

  reset(): void {
    this.started = false;
    this.dxPrev = 0;
  }
}

/** Three independent 1-Euro filters, one per axis. */
export class OneEuroVec3 {
  private fx: OneEuro;
  private fy: OneEuro;
  private fz: OneEuro;

  constructor(minCutoff = 1.2, beta = 0.03, dCutoff = 1.0) {
    this.fx = new OneEuro(minCutoff, beta, dCutoff);
    this.fy = new OneEuro(minCutoff, beta, dCutoff);
    // Z from a monocular model is noticeably noisier than X/Y, so it gets a
    // lower base cutoff (more smoothing at rest) but the same beta.
    this.fz = new OneEuro(minCutoff * 0.6, beta, dCutoff);
  }

  filter(v: Vec3, tSec: number, out: Vec3): Vec3 {
    out.x = this.fx.filter(v.x, tSec);
    out.y = this.fy.filter(v.y, tSec);
    out.z = this.fz.filter(v.z, tSec);
    return out;
  }

  reset(): void {
    this.fx.reset();
    this.fy.reset();
    this.fz.reset();
  }
}
